import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { erfuelltRecht } from '@symphenia/shared-types';
import { usersApi } from '@/lib/users/users.api';
import { InviteUserDialog } from '../components/InviteUserDialog';
import { OhneZugangDialog } from '../components/OhneZugangDialog';
import { ZugangGebenDialog } from '../components/ZugangGebenDialog';
import { ChangeEmailDialog } from '../components/ChangeEmailDialog';
import {
  ProjectRolesDialog,
  FESTE_PROJEKTROLLEN,
  type ProjektPhase,
} from '../components/ProjectRolesDialog';
import type { User, ListUsersParams } from '@/lib/users/user.types';
import { rolesApi, type RoleView, type EffectiveRights } from '@/lib/roles/roles.api';
import { apiErrorText } from '@/lib/api-error';
import { fmtDate } from '@/lib/format-date';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { useTerminology } from '@/lib/useTerminology';
import { Modal } from '@symphenia/ui-components';
import { bestaetige } from '@/lib/dialoge';

/**
 * AP-30 Stufe 3 — die Farbe hängt nicht mehr am Rang.
 *
 * Eine Farbtabelle über fünf feste Rollen lässt sich nicht fortschreiben,
 * sobald ein Mandant eigene anlegt. Systemrollen bleiben erkennbar, alles
 * Eigene bekommt eine Farbe: Der Unterschied, der zählt, ist „vorgegeben" gegen
 * „selbst gebaut" — nicht „hoch" gegen „niedrig".
 */
const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-primary/10 text-primary',
  manager: 'bg-primary/15 text-primary',
  employee: 'bg-primary/10 text-primary',
  time_booking: 'bg-primary/10 text-primary',
  readonly: 'bg-muted text-muted-foreground',
};
const ROLE_COLOR_CUSTOM = 'bg-accent/20 text-foreground';

// Zustellstatus der letzten Einladungs-Mail (per Resend-Webhook gesetzt).
const EMAIL_STATUS_STYLES: Record<string, string> = {
  sent: 'bg-muted text-muted-foreground',
  delivered: 'bg-green-100 text-green-700',
  delivery_delayed: 'bg-amber-100 text-amber-800',
  bounced: 'bg-red-100 text-red-700',
  complained: 'bg-red-100 text-red-700',
};

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthStore();

  /**
   * Die Rollen dieses Mandanten — aus dem Server, nicht aus einer festen Liste.
   *
   * Bis AP-30 standen hier vier Rollennamen im Code. Ein Mandant, der eine
   * eigene Rolle anlegt, hätte sie hier nie vergeben können.
   */
  const [rollen, setRollen] = useState<RoleView[]>([]);
  useEffect(() => {
    // Fehlschlag ist erträglich: Dann bleibt die Auswahl leer, statt die Seite
    // zu blockieren — die Liste selbst ist die Hauptsache dieser Maske.
    rolesApi.list().then(setRollen).catch(() => undefined);
  }, []);

  /** Systemrollen heißen in der Sprache des Benutzers, eigene tragen ihren Namen. */
  const rollenName = (r: RoleView) =>
    r.isSystem
      ? t(`roles.templates.${r.templateKey}.name`, { defaultValue: r.name })
      : r.name;

  const rollenFarbe = (key?: string) =>
    (key && ROLE_COLORS[key]) || ROLE_COLOR_CUSTOM;

  const [users, setUsers] = useState<User[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // AP-38-F: Mitarbeiter ohne Zugang anlegen und ihnen später Zugang geben.
  const [ohneZugangOpen, setOhneZugangOpen] = useState(false);
  const [zugangFuer, setZugangFuer] = useState<User | null>(null);

  const [filters, setFilters] = useState<ListUsersParams>({
    search: '',
    page: 1,
    limit: 20,
  });

  // AP-30-D: je Handlung das Recht ihres Endpunkts, statt einer Rollenliste
  // für alles. Wer einladen darf, muss keine Adressen ändern dürfen.
  const canManage = erfuelltRecht('core.user.update', currentUser?.permissions);
  const canInvite = canManage;
  const canManageEmail = erfuelltRecht('core.user.update_email', currentUser?.permissions);
  const canManageRoles = erfuelltRecht('core.role.manage', currentUser?.permissions);

  /**
   * AP-35 — Projektrollen gibt es nur, wo das Projektmanagement gebucht ist.
   * Die Phasen liest die Seite direkt über die API statt über das
   * Projekt-Feature: Features importieren nicht quer (AP-21-B).
   */
  const hasModule = useAuthStore((s) => s.hasModule);
  const mitProjektrollen = hasModule('PROJECT_MGMT.CORE');
  const canEditProjectRoles = mitProjektrollen && canManage;
  const darfPhasenLesen = erfuelltRecht('projects.project.read', currentUser?.permissions);
  const [phasen, setPhasen] = useState<ProjektPhase[]>([]);
  const [phasenFehler, setPhasenFehler] = useState(false);
  const [projektrollenVon, setProjektrollenVon] = useState<User | null>(null);
  useEffect(() => {
    if (!mitProjektrollen || !darfPhasenLesen) return;
    api
      .get<{ data: { key: string; name: string; isActive: boolean; sortOrder: number }[] }>(
        '/projects/settings/phases',
      )
      .then(({ data }) =>
        setPhasen(
          [...data.data]
            .filter((p) => p.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((p) => ({ key: p.key, name: p.name })),
        ),
      )
      // Ohne Phasen bleiben die festen Rollen vergebbar — kein Grund, die
      // Benutzerverwaltung zu blockieren.
      .catch(() => setPhasenFehler(true));
  }, [mitProjektrollen, darfPhasenLesen]);

  /** Beschriftung einer Projektrolle; eine abgeschaltete Phase zeigt ihren Schlüssel. */
  const projektrollenName = (key: string) =>
    (FESTE_PROJEKTROLLEN as readonly string[]).includes(key)
      ? t(`users.projectRoles.${key}`)
      : (phasen.find((p) => p.key === key)?.name ?? key);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: ListUsersParams = { ...filters };
      if (!params.search) delete params.search;
      const { data } = await usersApi.list(params);
      setUsers(data.data);
      setMeta(data.meta);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Auto-Aktualisierung: Solange eine Einladung noch im Zwischenstatus hängt
  // ('sent' = gerade versendet, 'delivery_delayed' = verzögert), pollt die Liste
  // alle 10 s, damit der per Webhook gesetzte Endstatus ohne manuellen Reload
  // erscheint. Stoppt automatisch, sobald nichts mehr aussteht (max. 2 Minuten).
  const hasPendingDelivery = users.some(
    (u) => u.lastEmailStatus === 'sent' || u.lastEmailStatus === 'delivery_delayed',
  );
  useEffect(() => {
    if (!hasPendingDelivery) return;
    const interval = setInterval(() => fetchUsers(), 10000);
    const stop = setTimeout(() => clearInterval(interval), 120000);
    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [hasPendingDelivery, fetchUsers]);

  const [resendingId, setResendingId] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [emailUser, setEmailUser] = useState<User | null>(null);

  // Löschen ist der einzige Weg, auf dem hier Daten wirklich verschwinden —
  // deshalb ein Dialog, der den Namen nennt, statt eines Klicks in der Zeile.
  /** AP-30-E: Für wen ist die Rechte-Auskunft offen? */
  const [rechteVon, setRechteVon] = useState<User | null>(null)
  const [deleteUser, setDeleteUser] = useState<User | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  /** Der Server hat endgültig abgelehnt — Wiederholen hilft nicht. */
  const [deleteFinal, setDeleteFinal] = useState(false)

  const confirmDelete = async () => {
    if (!deleteUser) return
    setDeleting(true)
    setDeleteError(null)
    try {
      const res = await usersApi.remove(deleteUser.id)
      setDeleteUser(null)
      // Was tatsächlich passiert ist, sagt der Server — „gelöscht" und
      // „Adresse freigegeben" sind zwei verschiedene Ausgänge, und der zweite
      // ist umkehrbar. Das gehört gesagt, nicht verschwiegen.
      setStatusMsg({ type: 'ok', text: res.data.message })
      setTimeout(() => setStatusMsg(null), 12000)
      fetchUsers()
    } catch (err: unknown) {
      const antwort = (err as { response?: { status?: number; data?: { message?: string } } })
        ?.response
      setDeleteError(apiErrorText(err, t('common.error')))
      // 409 heißt: Der Zustand steht dem entgegen (aktiv, letzter Administrator,
      // man selbst), und ein zweiter Klick ändert daran nichts. Ein Netzfehler
      // dagegen darf wiederholt werden.
      setDeleteFinal(antwort?.status === 409)
    } finally {
      setDeleting(false)
    }
  }

  const toggleActive = async (u: User) => {
    try {
      await usersApi.update(u.id, { isActive: !u.isActive });
      fetchUsers();
    } catch {
      // silently fail
    }
  };

  const resendInvite = async (u: User) => {
    setResendingId(u.id);
    setStatusMsg(null);
    try {
      await usersApi.resendInvite(u.id);
      setStatusMsg({ type: 'ok', text: t('users.inviteResent') });
    } catch (err: unknown) {
      setStatusMsg({ type: 'err', text: apiErrorText(err, t('common.error')) });
    } finally {
      setResendingId(null);
      setTimeout(() => setStatusMsg(null), 6000);
    }
  };

  /**
   * AP-38-E (8.9): Wer sein Gerät verloren hat und keinen Wiederherstellungscode
   * mehr hat. Arbeitet die Person auch für eine andere Firma oder ist sie extern,
   * lehnt der Server ab und nennt den Betreiber — die Meldung ist die Antwort.
   */
  const resetTwoFactor = async (u: User) => {
    const name = [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email;
    if (!(await bestaetige(t('zweiFaktor.zuruecksetzenFrage', { name }), { ok: t('zweiFaktor.zuruecksetzen'), gefaehrlich: true }))) return;
    setStatusMsg(null);
    try {
      await usersApi.resetTwoFactor(u.id);
      setStatusMsg({ type: 'ok', text: t('zweiFaktor.zurueckgesetzt') });
      fetchUsers();
    } catch (err: unknown) {
      setStatusMsg({ type: 'err', text: apiErrorText(err, t('common.error')) });
    } finally {
      setTimeout(() => setStatusMsg(null), 8000);
    }
  };

  /**
   * Setzt die Rolle dieses Benutzers auf **genau diese**.
   *
   * Trug er mehrere, ersetzt das alle — die Oberfläche fragt vorher, weil der
   * Verlust sonst unsichtbar wäre. Mehrere Rollen vergibt man in der
   * Rollenverwaltung; hier steht „die Rolle".
   */
  const changeRole = async (u: User, roleKey: string) => {
    if (u.roles.length > 1) {
      const namen = u.roles.map((r) => r.name).join(', ');
      const weiter = await bestaetige(
        t('users.replaceRolesConfirm', {
          defaultValue:
            'Dieser Benutzer trägt mehrere Rollen ({{roles}}). Die Auswahl hier ersetzt sie alle. Fortfahren?',
          roles: namen,
        }),
      );
      if (!weiter) return;
    }
    try {
      await usersApi.update(u.id, { roleKey });
      fetchUsers();
    } catch (err: unknown) {
      // **Nicht** verschlucken. Genau hier schlagen die beiden Regeln der
      // Rollenverwaltung zu — „niemand vergibt mehr, als er selbst hat" und
      // „der letzte Rollenverwalter bleibt" —, und ein stiller Fehlschlag
      // sieht aus wie eine Oberfläche, die nichts tut: Die Auswahl springt
      // beim nächsten Laden zurück, ohne dass jemand erfährt, warum.
      setStatusMsg({ type: 'err', text: apiErrorText(err, t('common.error')) });
      setTimeout(() => setStatusMsg(null), 8000);
      fetchUsers();
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('nav.users')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {meta.total} {t('users.count_other')}
          </p>
        </div>
        {canInvite && (
          <div className="flex flex-wrap gap-2">
            {/* AP-38-F: ein Mitarbeiter, der sich nie anmeldet — ohne Einladung, Adresse freiwillig */}
            <button
              onClick={() => setOhneZugangOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              + {t('users.ohneZugang.knopf')}
            </button>
            <button
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              + {t('users.invite')}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <input
          type="text"
          placeholder={t('common.search') + '…'}
          value={filters.search ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))
          }
          className="w-64 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <select
          value={filters.roleKey ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, roleKey: e.target.value || undefined, page: 1 }))
          }
          className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">{t('users.allRoles')}</option>
          {rollen.map((r) => (
            <option key={r.key} value={r.key}>
              {rollenName(r)}
            </option>
          ))}
        </select>
        <select
          value={filters.isActive === undefined ? '' : String(filters.isActive)}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              isActive: e.target.value === '' ? undefined : e.target.value === 'true',
              page: 1,
            }))
          }
          className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">{t('common.allStatuses')}</option>
          <option value="true">{t('common.active')}</option>
          <option value="false">{t('common.inactive')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : users.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
            {t('users.noUsers')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {t('users.firstName')} / {t('users.lastName')}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {t('auth.email')}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {t('users.role')}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {t('common.status')}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {t('users.lastLogin')}
                </th>
                {canManage && (
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                        {(u.profile?.firstName?.[0] ?? u.email?.[0] ?? '?').toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {u.profile?.firstName && u.profile?.lastName
                            ? `${u.profile.firstName} ${u.profile.lastName}`
                            : '–'}
                        </div>
                        {u.profile?.jobTitle && (
                          <div className="text-xs text-muted-foreground">{u.profile.jobTitle}</div>
                        )}
                        {mitProjektrollen && (u.profile?.projectRoles?.length ?? 0) > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(u.profile?.projectRoles ?? []).map((key) => (
                              <span
                                key={key}
                                className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                              >
                                {projektrollenName(key)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    {canManage && u.id !== currentUser?.id ? (
                      <select
                        value={u.roles[0]?.key ?? ''}
                        onChange={(e) => changeRole(u, e.target.value)}
                        title={
                          u.roles.length > 1
                            ? t('users.multipleRoles', {
                                defaultValue: 'Trägt mehrere Rollen: {{roles}}',
                                roles: u.roles.map((r) => r.name).join(', '),
                              })
                            : undefined
                        }
                        className={`rounded-full px-2 py-0.5 text-xs font-medium border-0 cursor-pointer ${rollenFarbe(u.roles[0]?.key)}`}
                      >
                        {/* AP-38-F: Ein Mitarbeiter aus der Lohnverrechnung hat keine Rolle —
                            ohne leere Option zeigte das Feld die erste, also „Administrator". */}
                        {u.roles.length === 0 && (
                          <option value="" disabled>
                            {t('users.keineRolle')}
                          </option>
                        )}
                        {rollen.map((r) => (
                          <option key={r.key} value={r.key}>
                            {rollenName(r)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${rollenFarbe(u.roles[0]?.key)}`}
                      >
                        {u.roles.map((r) => r.name).join(', ') || '—'}
                      </span>
                    )}
                    {u.roles.length > 1 && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        +{u.roles.length - 1}
                      </span>
                    )}
                    {/* AP-30-E: Die Frage, die eine Rechteliste erst benutzbar
                        macht. Der Unterschied zwischen vergeben und wirksam ist
                        immer ein fehlendes Modul oder ein fehlender Platz — und
                        genau diese Auskunft führt zum richtigen
                        Ansprechpartner. */}
                    {canManageRoles && (
                      <button
                        type="button"
                        onClick={() => setRechteVon(u)}
                        className="ml-2 text-xs text-primary hover:underline"
                      >
                        {t('users.showRights', { defaultValue: 'Rechte' })}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        u.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {u.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                    {/* AP-38-F: meldet sich nie an — steht trotzdem in jeder Mitarbeiterliste */}
                    {u.ohneZugang && (
                      <span
                        className="ml-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                        title={t('users.ohneZugang.kennzeichenHinweis')}
                      >
                        {t('users.ohneZugang.kennzeichen')}
                      </span>
                    )}
                    {/* AP-38-C: arbeitet für die Firma, ist aber nicht hier beschäftigt */}
                    {u.kind === 'EXTERNAL' && (
                      <span
                        className="ml-1 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
                        title={t('users.externalHint')}
                      >
                        {t('users.external')}
                      </span>
                    )}
                    {/* AP-38-E: den Übergang verfolgen — wer hat die Zwei-Faktor-Anmeldung eingerichtet */}
                    {u.zweiFaktor && u.isActive && (
                      <div className="mt-1">
                      <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                          u.zweiFaktor.eingerichtet
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                        title={t('zweiFaktor.titel')}
                      >
                        {t('zweiFaktor.spalte')}{' '}
                        {u.zweiFaktor.eingerichtet
                          ? t('zweiFaktor.eingerichtet')
                          : currentUser?.zweiFaktor?.frist
                            ? t('zweiFaktor.ausstehendBis', { datum: fmtDate(currentUser.zweiFaktor.frist) })
                            : t('zweiFaktor.ausstehend')}
                      </span>
                      </div>
                    )}
                    {u.lastEmailStatus && (
                      <div className="mt-1">
                        <span
                          title={u.lastEmailError ?? undefined}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            EMAIL_STATUS_STYLES[u.lastEmailStatus] ?? 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {t(`users.delivery.${u.lastEmailStatus}`, {
                            defaultValue: t('users.delivery.sent'),
                          })}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleDateString()
                      : '–'}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      {/* Die eigenen Projektrollen darf man setzen — wer das
                          Projekt leitet, ist oft der, der hier verwaltet. */}
                      {u.id === currentUser?.id && canEditProjectRoles && (
                        <button
                          onClick={() => setProjektrollenVon(u)}
                          className="text-xs font-medium text-primary hover:text-primary/80"
                        >
                          {t('users.projectRoles.edit')}
                        </button>
                      )}
                      {u.id !== currentUser?.id && (
                        <div className="flex items-center justify-end gap-3">
                          {/* Einladung erneut senden – auch für bereits aktive Nutzer,
                              die nie eine E-Mail erhielten und kein Passwort setzen konnten. */}
                          {u.ohneZugang ? (
                            // AP-38-F: statt „Einladung erneut senden" — er hatte nie eine.
                            <button
                              onClick={() => setZugangFuer(u)}
                              className="text-xs font-medium text-primary hover:text-primary/80"
                            >
                              {t('users.zugangGeben.aktion')}
                            </button>
                          ) : (
                            <button
                              onClick={() => resendInvite(u)}
                              disabled={resendingId === u.id}
                              className="text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                            >
                              {resendingId === u.id ? t('common.loading') : t('users.resendInvite')}
                            </button>
                          )}
                          {canEditProjectRoles && (
                            <button
                              onClick={() => setProjektrollenVon(u)}
                              className="text-xs font-medium text-primary hover:text-primary/80"
                            >
                              {t('users.projectRoles.edit')}
                            </button>
                          )}
                          {u.zweiFaktor?.eingerichtet && (
                            <button
                              onClick={() => void resetTwoFactor(u)}
                              className="text-xs font-medium text-primary hover:text-primary/80"
                            >
                              {t('zweiFaktor.zuruecksetzen')}
                            </button>
                          )}
                          {/* AP-38-C: Die Adresse einer externen Person ändert nur sie selbst */}
                          {canManageEmail && !u.isExternallyManaged && u.kind !== 'EXTERNAL' && (
                            <button
                              onClick={() => setEmailUser(u)}
                              className="text-xs font-medium text-primary hover:text-primary/80"
                            >
                              {t('users.changeEmail')}
                            </button>
                          )}
                          <button
                            onClick={() => toggleActive(u)}
                            className={`text-xs font-medium ${
                              u.isActive ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'
                            }`}
                          >
                            {u.isActive ? t('common.deactivate') : t('common.activate')}
                          </button>
                          {/* Löschen erst, wenn der Benutzer deaktiviert ist: zwei
                              bewusste Schritte statt eines Klicks neben dem
                              falschen Namen. Der Server verlangt es ohnehin. */}
                          {canManageEmail && !u.isActive && (
                            <button
                              onClick={() => {
                                setDeleteError(null)
                                setDeleteFinal(false)
                                setDeleteUser(u)
                              }}
                              className="text-xs font-medium text-red-500 hover:text-red-700"
                            >
                              {t('common.delete')}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {(meta.page - 1) * meta.limit + 1}–
            {Math.min(meta.page * meta.limit, meta.total)} / {meta.total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={meta.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              className="rounded border border-input px-3 py-1 disabled:opacity-40 hover:bg-muted/50"
            >
              ‹
            </button>
            <button
              disabled={meta.page >= meta.totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              className="rounded border border-input px-3 py-1 disabled:opacity-40 hover:bg-muted/50"
            >
              ›
            </button>
          </div>
        </div>
      )}

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={fetchUsers}
      />

      <OhneZugangDialog open={ohneZugangOpen} onClose={() => setOhneZugangOpen(false)} onSuccess={fetchUsers} />

      <ZugangGebenDialog
        user={zugangFuer}
        onClose={() => setZugangFuer(null)}
        onSuccess={(text, art) => {
          setStatusMsg({ type: art, text });
          fetchUsers();
          setTimeout(() => setStatusMsg(null), 6000);
        }}
      />

      <ChangeEmailDialog
        user={emailUser}
        onClose={() => setEmailUser(null)}
        onSuccess={fetchUsers}
      />

      {projektrollenVon && (
        <ProjectRolesDialog
          user={projektrollenVon}
          phasen={phasen}
          phasenFehler={phasenFehler}
          onClose={() => setProjektrollenVon(null)}
          onSaved={fetchUsers}
        />
      )}

      {rechteVon && <RechteDialog user={rechteVon} onClose={() => setRechteVon(null)} />}

      <DeleteUserDialog
        user={deleteUser}
        busy={deleting}
        error={deleteError}
        final={deleteFinal}
        onClose={() => setDeleteUser(null)}
        onConfirm={confirmDelete}
      />

      {/* Status-Meldung: Einladungsversand und Ergebnis einer Entfernung */}
      {statusMsg && (
        <div
          className={`fixed bottom-4 right-4 z-50 max-w-sm rounded-md border px-4 py-3 text-sm shadow-lg ${
            statusMsg.type === 'ok'
              ? 'border-green-300 text-green-800'
              : 'border-destructive/40 text-destructive'
          }`}
          style={{ background: 'hsl(var(--card))' }}
          role="status"
        >
          {statusMsg.text}
        </div>
      )}
    </div>
  );
}


/**
 * Der einzige Dialog in der Benutzerverwaltung, nach dem etwas wirklich weg ist.
 *
 * Er nennt deshalb **Name und Adresse** im Klartext und sagt vorher, was das
 * Löschen bewirkt und was nicht. Die Absage des Servers („142 Zeitbuchungen …")
 * erscheint hier unverändert: Sie ist konkreter als jeder Satz, der hier fest
 * stehen könnte, und enthält den Ausweg.
 */
function DeleteUserDialog({
  user,
  busy,
  error,
  final: endgueltig,
  onClose,
  onConfirm,
}: {
  user: User | null
  busy: boolean
  error: string | null
  /** Der Server hat abgelehnt, und der Grund ändert sich durch Wiederholen nicht. */
  final: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  if (!user) return null

  const name = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ')

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={t('users.deleteTitle')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted"
          >
            {endgueltig ? t('common.close') : t('common.cancel')}
          </button>
          {/* Nach einer endgültigen Absage (aktiv, letzter Administrator) bleibt
              nur Schließen: Ein roter Knopf, der auf jeden Klick dieselbe
              Meldung zeigt, sieht aus wie ein Defekt. Was zu tun ist, steht in
              der Meldung. */}
          {!endgueltig && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('users.deleteConfirm')}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-foreground">
          {name || user.email} <span className="text-muted-foreground">({user.email})</span>
        </p>
        {!endgueltig && <p className="text-muted-foreground">{t('users.deleteExplain')}</p>}
        {error && <p className="text-destructive">{error}</p>}
      </div>
    </Modal>
  )
}

/**
 * AP-30-E — „Welche Rechte hat dieser Mensch tatsächlich?"
 *
 * Zeigt **beides**: was seine Rollen vergeben und was davon wirkt. Der
 * Unterschied ist immer ein fehlendes Modul oder ein fehlender Platz, und nur
 * diese Unterscheidung führt zum richtigen Ansprechpartner — Vertrieb gegen
 * eigenen Administrator (AP-29-C). Ohne sie sähe ein Administrator ein Häkchen,
 * das er gesetzt hat, und eine Oberfläche, in der nichts passiert.
 */
function RechteDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const [stand, setStand] = useState<EffectiveRights | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    rolesApi
      .effectiveFor(user.id)
      .then(setStand)
      .catch(() =>
        setFehler(t('users.rightsFailed', { defaultValue: 'Die Rechte konnten nicht geladen werden.' })),
      )
  }, [user.id, t])

  const wirksam = new Set(stand?.effective ?? [])
  const name = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ')

  return (
    <Modal isOpen onClose={onClose} size="md" title={t('users.showRights', { defaultValue: 'Rechte' })}>
      <div className="space-y-3 text-sm">
        <p className="text-foreground">
          {name || user.email} <span className="text-muted-foreground">({user.email})</span>
        </p>

        {fehler && <p className="text-destructive">{fehler}</p>}
        {!stand && !fehler && <p className="text-muted-foreground">{t('common.loading')}</p>}

        {stand && (
          <>
            <p className="text-muted-foreground">
              {t('users.rightsRoles', { defaultValue: 'Rollen:' })}{' '}
              {stand.roles.map((r) => r.name).join(', ') || '—'}
            </p>

            {stand.granted.length === 0 && (
              <p className="text-muted-foreground">
                {t('users.rightsNone', { defaultValue: 'Keine Rechte vergeben.' })}
              </p>
            )}

            <ul className="max-h-80 space-y-0.5 overflow-y-auto">
              {[...stand.granted].sort().map((p) => (
                <li
                  key={p}
                  className={wirksam.has(p) ? 'text-foreground' : 'text-muted-foreground line-through'}
                  title={
                    wirksam.has(p)
                      ? undefined
                      : t('users.rightsInactiveHint', {
                          defaultValue:
                            'Vergeben, aber wirkungslos: Das Modul ist nicht gebucht oder dieser Benutzer hat keinen Platz darin.',
                        })
                  }
                >
                  {t(`permissions.${p}`, { defaultValue: p, projectPlural: terms.projectPlural })}
                </li>
              ))}
            </ul>

            <p className="text-xs text-muted-foreground">
              {t('users.rightsLegend', {
                defaultValue:
                  'Durchgestrichen heißt: vergeben, aber wirkungslos — das Modul fehlt oder der Platz darin.',
              })}
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}
